import React, { memo, useState, useCallback, useRef } from 'react';
import { Send, Smile, Paperclip, X, Image, FileText, Film } from 'lucide-react';
import EmojiPicker from 'emoji-picker-react';
import api from '../services/api';

const MessageInput = ({ onSendMessage, disabled = false, darkMode, onTyping, conversaId, instanciaId, onMediaSent }) => {
  const [mensagem, setMensagem] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const emojiPickerRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const handleChange = (e) => {
    setMensagem(e.target.value);
    if (onTyping && !typingTimeoutRef.current) {
      onTyping();
      typingTimeoutRef.current = setTimeout(() => { typingTimeoutRef.current = null; }, 2000);
    }
  };

  const handleSend = useCallback(() => {
    if (file && conversaId) {
      setSending(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('conversaId', conversaId);
      if (instanciaId) formData.append('instanciaId', instanciaId);
      if (mensagem.trim()) formData.append('caption', mensagem.trim());
      api.post('/atendimento/enviar-midia', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }).then(() => {
        setFile(null);
        setPreview(null);
        setMensagem('');
        onMediaSent?.();
      }).catch(e => console.error('Erro ao enviar mídia:', e))
        .finally(() => setSending(false));
      return;
    }
    if (mensagem.trim() && !disabled) {
      onSendMessage(mensagem.trim());
      setMensagem('');
    }
  }, [mensagem, onSendMessage, disabled, file, conversaId, instanciaId, onMediaSent]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  }, [handleSend]);

  const handleEmojiClick = (emojiData) => {
    setMensagem(prev => prev + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    if (f.type.startsWith('image/') || f.type.startsWith('video/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setPreview({ url: ev.target.result, type: f.type.startsWith('image/') ? 'image' : 'video' });
      reader.readAsDataURL(f);
    } else {
      setPreview({ type: 'file', name: f.name });
    }
    e.target.value = '';
  };

  const clearFile = () => { setFile(null); setPreview(null); };

  const fileIcon = preview?.type === 'image' ? <Image size={16} /> : preview?.type === 'video' ? <Film size={16} /> : <FileText size={16} />;

  return (
    <div className={`border-t ${darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
      {/* File preview */}
      {preview && (
        <div className={`px-4 pt-3 flex items-center gap-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          {preview.type === 'image' && <img src={preview.url} alt="" className="w-16 h-16 object-cover rounded-lg" />}
          {preview.type === 'video' && <video src={preview.url} className="w-16 h-16 object-cover rounded-lg" />}
          {preview.type === 'file' && (
            <div className={`w-16 h-16 rounded-lg flex items-center justify-center ${darkMode ? 'bg-zinc-700' : 'bg-gray-200'}`}>
              <FileText size={24} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm truncate">{file?.name}</p>
            <p className="text-xs opacity-50">{(file?.size / 1024).toFixed(0)} KB</p>
          </div>
          <button onClick={clearFile} className="p-1 rounded-full hover:bg-black/10"><X size={18} /></button>
        </div>
      )}

      <div className="flex items-center space-x-2 sm:space-x-3 p-2 sm:p-4">
        <input type="file" ref={fileInputRef} className="hidden" accept="image/*,video/*,audio/*,.pdf,.doc,.docx" onChange={handleFileSelect} />

        {/* Emoji */}
        <div className="relative" ref={emojiPickerRef}>
          <button onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            className={`p-2 rounded-full transition-colors ${darkMode ? 'hover:bg-zinc-700 text-gray-400' : 'hover:bg-gray-200 text-gray-600'}`}>
            <Smile className="w-5 h-5" />
          </button>
          {showEmojiPicker && (
            <div className="absolute bottom-12 left-0 z-50">
              <EmojiPicker onEmojiClick={handleEmojiClick} theme={darkMode ? 'dark' : 'light'} width={320} height={400} />
            </div>
          )}
        </div>

        {/* Attach */}
        <button onClick={() => fileInputRef.current?.click()}
          className={`p-2 rounded-full transition-colors ${darkMode ? 'hover:bg-zinc-700 text-gray-400' : 'hover:bg-gray-200 text-gray-600'}`}>
          <Paperclip className="w-5 h-5" />
        </button>

        <div className="flex-1 relative">
          <input type="text" value={mensagem} onChange={handleChange} onKeyPress={handleKeyPress}
            placeholder={file ? "Legenda (opcional)..." : "Digite uma mensagem..."}
            disabled={disabled || sending}
            className={`w-full rounded-full px-3 py-2 sm:px-4 sm:py-3 focus:outline-none focus:ring-2 focus:ring-green-500 ${
              darkMode ? 'bg-zinc-700 text-white placeholder-gray-400 disabled:bg-zinc-800' : 'bg-white text-gray-900 placeholder-gray-500 disabled:bg-gray-100'
            }`} />
        </div>

        <button onClick={handleSend} disabled={(!mensagem.trim() && !file) || disabled || sending}
          className={`p-2.5 sm:p-3 rounded-full transition-colors ${
            (!mensagem.trim() && !file) || disabled || sending
              ? darkMode ? 'bg-gray-600 text-gray-400 cursor-not-allowed' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-green-600 hover:bg-green-700 text-white'
          }`}>
          {sending ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Send className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );
};

export default memo(MessageInput);
